import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Tag, Avatar, Button, message, cn, SectionHead, SECTION_TINT, type TagTone } from '../../ui';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { InlineEditField } from '../../components/InlineEditField';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Phone, MessageCircle, MapPin, User, ExternalLink, Sprout, Users, CalendarDays,
  FileText, Camera, ShieldCheck, Languages, Navigation,
} from 'lucide-react';
import { STATE_NAME } from '../../constants/indianStates';
import { formatIstDate } from '../../lib/time';
import { displayMobile } from '../../lib/validators';
import { DocumentPreview } from '../../components/DocumentPreview';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { CopyValue } from '../../components/CopyValue';
import { RoleBadge } from '../../components/RoleBadge';
import { TerritoryChips } from '../../components/TerritoryChips';
import { PALETTE } from '../../lib/palette';

// Self-contained SVG pin (Leaflet's default marker images don't resolve under bundlers).
const PIN = L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 28],
  html: `<svg viewBox="0 0 24 32" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${PALETTE.brand[600]}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/></svg>`,
});

// Leaflet mis-measures while the dialog is still animating in — recompute once it has settled.
function FixSize() {
  const map = useMap();
  useEffect(() => { const t = setTimeout(() => map.invalidateSize(), 260); return () => clearTimeout(t); }, [map]);
  return null;
}

const STATUS_COLOR: Record<string, TagTone> = { ACTIVE: 'green', INACTIVE: 'red' };

/** A derived fact — same shape as an InlineEditField so a mixed grid still reads as one list. */
function OnlyField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-1 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

/** A field group: heading, then its own two-column grid. */
function Group({ icon, tint, title, hint, cols = 'sm:grid-cols-2', children }: {
  icon: ReactNode; tint: string; title: string; hint?: ReactNode; cols?: string; children: ReactNode;
}) {
  return (
    <section>
      <SectionHead icon={icon} tint={tint} hint={hint}>{title}</SectionHead>
      <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3.5', cols)}>{children}</dl>
    </section>
  );
}

/**
 * A value that carries its own meaning as a chip — a phone number is a thing you call, a pincode and
 * a coordinate pair are things you copy and must not be misread. Grey text for all three said none
 * of that.
 */
function Chip({ tone, mono, icon, children }: {
  tone: 'green' | 'sky' | 'violet' | 'slate'; mono?: boolean; icon?: ReactNode; children: ReactNode;
}) {
  const tones = {
    green: 'bg-emerald-500/10 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/25',
    sky: 'bg-sky-500/10 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/25',
    violet: 'bg-violet-500/10 text-violet-700 ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/25',
    slate: 'bg-slate-500/10 text-slate-700 ring-slate-600/20 dark:bg-slate-400/10 dark:text-slate-300 dark:ring-slate-400/25',
  };
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1.5 rounded-control px-2 py-0.5 ring-1 ring-inset',
      tones[tone], mono ? 'font-mono text-[12.5px]' : 'type-body-strong')}>
      {icon}<span className="truncate">{children}</span>
    </span>
  );
}

/**
 * One document, in its own frame.
 *
 * The photo and the KYC scan used to sit in a single bordered strip as two loose columns, so the
 * "KYC DOCUMENT" heading appeared to caption the photo thumbnail beside it — the reader could not
 * tell which label owned which picture. A frame each removes the ambiguity, and the state (on file /
 * missing) is stated on the card rather than inferred from whether a thumbnail rendered.
 */
function DocCard({ icon, title, present, children }: {
  icon: ReactNode; title: string; present: boolean; children: ReactNode;
}) {
  return (
    <div className={cn('rounded-control border p-2.5 transition-colors',
      present
        ? 'border-line bg-surface'
        : 'border-warning-300/60 bg-warning-50/50 dark:border-harvest-500/30 dark:bg-harvest-500/5')}>
      <div className="mb-2 flex items-center gap-1.5">
        <span className={cn('shrink-0', present ? 'text-subtle' : 'text-warning-600 dark:text-harvest-400')}>{icon}</span>
        <span className="type-caption-strong text-content">{title}</span>
      </div>
      {children}
    </div>
  );
}

/** Small "default" action rendered as an anchor — link actions must navigate via href, and the ui
 *  Button is a <button> (no href), so anchors preserve the tel:/wa.me/maps navigation. */
const ACTION_BTN = 'inline-flex h-8 items-center justify-center gap-1.5 rounded-control border border-line bg-surface px-3 type-body-strong leading-none text-content shadow-e1 transition-colors duration-150 hover:border-brand-400 hover:text-brand-700 dark:hover:border-brand-500';

/** Every KYC document on file, tolerating farmers saved before the list column existed. */
function kycDocs(f: any): string[] {
  const many = Array.isArray(f?.kycDocUrls) ? f.kycDocUrls.filter(Boolean) : [];
  if (many.length) return many;
  return f?.kycDocUrl ? [f.kycDocUrl] : [];
}

const LANGUAGE_NAME: Record<string, string> = { hi: 'Hindi', en: 'English', mr: 'Marathi' };

/** Whole years, the way an age is spoken. Null for a missing or nonsense date. */
function ageFrom(dob: any): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (!Number.isFinite(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a >= 0 && a < 130 ? a : null;
}

/**
 * Read-only farmer profile as a CENTRED DIALOG (it was a 460px slide-over).
 *
 * The record outgrew the panel: identity, twelve fields, two documents and a map stacked into one
 * narrow column meant scrolling past everything to reach the map, with the page dimmed behind. A
 * dialog gets roughly twice the width, so the facts sit beside the documents and location instead
 * of below them, and nothing needs scrolling on a normal screen.
 *
 * The fields are GROUPED (identity / contact / where / farm / coverage / record) rather than run
 * together as one fifteen-row list. Flat, every field looked equally important and the eye had
 * nothing to anchor on; the reader said so — "make all these look better and colorful, currently it
 * is hard to decide which one is header/label". Each group carries its own coloured mark, and the
 * three levels are now visibly different weights: SECTION / label / **value**.
 *
 * Works for any viewer with FARMER.VIEW (no edit rights needed); built from the list row, which
 * carries the full entity plus the resolved territory/owner names.
 */
export function FarmerDetailModal({ farmer, open, onClose, onEdit }: {
  farmer: any;
  open: boolean;
  onClose: () => void;
  /** Omitted when the viewer lacks FARMER.EDIT — the page owns that decision, not this dialog. */
  onEdit?: () => void;
}) {
  // Mount Leaflet only AFTER the dialog's entrance finishes, so it measures a full-size container
  // and actually loads tiles — mounting mid-animation leaves them blank (the pin renders, no map).
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!open) { setSettled(false); return; }
    const t = setTimeout(() => setSettled(true), 260);
    return () => clearTimeout(t);
  }, [open]);

  /**
   * The list row is enough to OPEN on, but not to edit from: it carries no `editableFields`, and
   * after an inline save it is stale. Re-read the record while the dialog is open, falling back to
   * the row so the dialog paints immediately instead of flashing empty.
   */
  const { data: fresh } = useQuery({
    queryKey: ['farmer', farmer?.id],
    queryFn: () => request<any>({ url: `/farmers/${farmer.id}` }),
    enabled: open && !!farmer?.id,
  });
  const f = fresh ?? farmer ?? {};
  const qc = useQueryClient();

  const editableFields = new Set<string>(((f as any)?.editableFields ?? []) as string[]);
  const saveField = async (patch: Record<string, any>) => {
    await request({ url: `/farmers/${farmer.id}`, method: 'PATCH', data: patch });
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['farmer', farmer.id] }),
      qc.invalidateQueries({ queryKey: ['farmers'] }),
    ]);
    message.success('Saved');
  };
  const fieldProps = (field: string) => ({
    editable: editableFields.has(field),
    lockReason: editableFields.has(field) ? undefined : 'Only an Admin can edit a farmer.',
    onSave: (v: any) => saveField({ [field]: v }),
  });

  // Reps who work this farmer's territory, each tagged with the area that puts them in scope - the
  // same endpoint the server validates the owner against, so the list offers only what it accepts.
  const { data: territorySps } = useQuery({
    queryKey: ['farmer-territory-sps', f?.territoryId],
    queryFn: () => request<any>({ url: '/territory-assignments/sales-persons', params: { territoryId: f?.territoryId } }),
    enabled: open && !!f?.territoryId,
  });
  const coverageSps = (territorySps?.salesPersons ?? []) as any[];
  /**
   * A farmer with NO territory has no coverage list — and an empty picker is a dead end, because the
   * server accepts any valid field owner in that case. Fall back to the staff directory so the field
   * is still usable, exactly where the server's own rule relaxes.
   */
  const { data: allStaff } = useQuery({
    queryKey: ['farmer-owner-fallback'],
    queryFn: () => requestAllPages('/users'),
    enabled: open && !!f?.id && !f?.territoryId,
  });
  const spOptions = (coverageSps.length
    ? coverageSps
    : ((allStaff?.data ?? []) as any[]).map((u: any) => ({ id: u.id, fullName: u.fullName, territory: null, territoryLevel: null }))
  ).map((u: any) => ({
    value: String(u.id), label: u.fullName ?? `#${u.id}`, coverage: u.territory, level: u.territoryLevel,
    primary: String(u.id) === String(territorySps?.defaultOwnerId ?? ''),
  }));
  const renderSpOption = (o: any) => (
    <span className="flex w-full min-w-0 flex-col gap-0.5 py-0.5">
      <span className="flex items-center gap-1.5"><span className="truncate">{o.label}</span>{o.primary && <Tag tone="green">primary</Tag>}</span>
      {o.coverage && <span className="truncate type-caption text-subtle">{o.coverage}{o.level ? ` · ${String(o.level).toLowerCase()}` : ''}</span>}
    </span>
  );
  const { data: farmerFacets } = useQuery({
    queryKey: ['farmer-facets'],
    queryFn: () => request<any>({ url: '/farmers/filter-options' }),
    enabled: open,
  });
  const cropOptions = ((farmerFacets?.crops ?? []) as any[]).map((c: any) => ({ value: String(c.value), label: String(c.label ?? c.value) }));

  const lat = Number(f.latitude), lng = Number(f.longitude);
  const hasPin = f.latitude != null && f.longitude != null && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  const stateName = f.stateCode ? (STATE_NAME.get(f.stateCode) ?? f.stateCode) : '';
  const place = [f.city, stateName].filter(Boolean).join(', ');
  const wa = String(f.whatsappNumber || f.mobile || '').replace(/[^0-9]/g, '');
  const kyc = kycDocs(f);
  const gmaps = hasPin
    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    : (place ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}` : null);

  // Territory / owner come named from the API (the row carries only their ids), so this can label the
  // assignment without pulling the territory tree and user directory into the browser.
  const territoryLabel = f.territoryName
    ? (f.territoryCode ? `${f.territoryCode} · ${f.territoryName}` : f.territoryName)
    : (f.territoryId ? `#${f.territoryId}` : null);
  const spLabel = f.assignedSalesPersonName ?? (f.assignedSalesPersonId ? `#${f.assignedSalesPersonId}` : null);
  const coords = hasPin ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : null;
  const age = ageFrom(f.dob);
  const crops: string[] = f.crops ?? [];

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Farmer details"
      width={980}
      footer={
        <>
          {onEdit && <Button variant="default" onClick={onEdit}>Edit</Button>}
          <Button variant="default" onClick={onClose}>Close</Button>
        </>
      }
    >
      {farmer && (
        <>
          {/* Letterhead: who this is, what state they are in, and the three things you do from here.
              Tinted so the identity band reads as the header of the record rather than a first row. */}
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card bg-gradient-to-r from-brand-600/10 via-brand-500/5 to-transparent p-3 ring-1 ring-inset ring-brand-600/10">
            <Avatar size={52} src={f.photoUrl} fallback={<User size={26} />}
              className="ring-2 ring-white shadow-e1 dark:ring-surface"
              style={{ background: 'var(--color-brand-600)' }} />
            <div className="min-w-0">
              <div className="type-section-title text-content">{f.fullName}</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Tag tone={STATUS_COLOR[f.status] ?? 'neutral'}>{f.status || 'ACTIVE'}</Tag>
                {/* KYC beside the lifecycle status. Derived from the document alone — a farmer has no
                    submit/verify workflow (retailers do), so "on file" is the strongest true claim. */}
                <Tag tone={kyc.length ? 'green' : 'gold'}>
                  {kyc.length ? (kyc.length > 1 ? `KYC on file · ${kyc.length}` : 'KYC on file') : 'KYC pending'}
                </Tag>
                {f.registeredVia && <Tag>{f.registeredVia}</Tag>}
              </div>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {f.mobile && <a className={ACTION_BTN} href={`tel:${f.mobile}`}><Phone size={16} /><span>Call</span></a>}
              {wa && <a className={ACTION_BTN} href={`https://wa.me/${wa}`} target="_blank" rel="noopener"><MessageCircle size={16} /><span>WhatsApp</span></a>}
              {gmaps && <a className={ACTION_BTN} href={gmaps} target="_blank" rel="noopener"><MapPin size={16} /><span>Google Maps</span></a>}
            </div>
          </div>

          {/* The width the dialog buys: facts beside documents + location, rather than under them. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            {/* Read/edit fields in named groups, NOT a Descriptions table that HID anything empty -
                a farmer with no date of birth showed no "Date of birth" row, so the one thing you
                came to fill in was the one thing you could not see. */}
            <div className="space-y-4 rounded-card border border-line bg-surface p-4 shadow-e1">
              <Group icon={<User className="size-3.5" />} tint={SECTION_TINT.identity} title="Identity">
                <InlineEditField label="Full name" value={f.fullName} {...fieldProps('fullName')}>{f.fullName}</InlineEditField>
                <InlineEditField label="Gender" value={f.gender} type="select"
                  options={[{ value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }, { value: 'OTHER', label: 'Other' }]}
                  {...fieldProps('gender')}>{f.gender}</InlineEditField>
                <InlineEditField label="Date of birth" value={f.dob} type="date" {...fieldProps('dob')}>
                  {f.dob ? (
                    <span className="flex flex-wrap items-baseline gap-1.5">
                      {formatIstDate(f.dob)}
                      {age != null && <span className="type-caption text-subtle">{age} yrs</span>}
                    </span>
                  ) : undefined}
                </InlineEditField>
                {/* The stored value is a two-letter code. "MR" is not a language anyone recognises,
                    so the name leads and the code follows it. */}
                <InlineEditField label="Language" value={f.languagePref} type="select"
                  options={[{ value: 'hi', label: 'Hindi' }, { value: 'en', label: 'English' }, { value: 'mr', label: 'Marathi' }]}
                  {...fieldProps('languagePref')}>
                  {f.languagePref ? (
                    <Chip tone="violet" icon={<Languages size={13} />}>
                      {LANGUAGE_NAME[String(f.languagePref).toLowerCase()] ?? String(f.languagePref).toUpperCase()}
                    </Chip>
                  ) : undefined}
                </InlineEditField>
              </Group>

              <Group icon={<Phone className="size-3.5" />} tint={SECTION_TINT.contact} title="Contact">
                <InlineEditField label="Mobile (WhatsApp)" value={f.mobile} className="sm:col-span-2" {...fieldProps('mobile')}>
                  {f.mobile ? (
                    <CopyValue value={f.mobile} label="Mobile">
                      <Chip tone="green" icon={<Phone size={13} />}>{displayMobile(f.mobile)}</Chip>
                    </CopyValue>
                  ) : undefined}
                </InlineEditField>
              </Group>

              <Group icon={<MapPin className="size-3.5" />} tint={SECTION_TINT.place} title="Where they are">
                <InlineEditField label="Address" value={f.address} type="textarea" className="sm:col-span-2" {...fieldProps('address')}>
                  {f.address}
                </InlineEditField>
                <InlineEditField label="Village / City" value={f.city} {...fieldProps('city')}>{f.city}</InlineEditField>
                <InlineEditField label="Pincode" value={f.pincode} {...fieldProps('pincode')}>
                  {f.pincode ? <Chip tone="sky" mono>{f.pincode}</Chip> : undefined}
                </InlineEditField>
                <OnlyField label="State">{stateName || undefined}</OnlyField>
                <OnlyField label="GPS coordinates">
                  {coords
                    ? <CopyValue value={coords} label="Coordinates"><Chip tone="sky" mono icon={<Navigation size={12} />}>{coords}</Chip></CopyValue>
                    : undefined}
                </OnlyField>
              </Group>

              <Group icon={<Sprout className="size-3.5" />} tint={SECTION_TINT.farm} title="Farm"
                hint={crops.length ? `${crops.length} crop${crops.length === 1 ? '' : 's'}` : undefined}>
                <InlineEditField label="Land (acres)" value={f.landAcres} type="number" {...fieldProps('landAcres')}>
                  {f.landAcres != null
                    ? <span className="flex items-baseline gap-1"><span className="type-metric-sm text-content">{Number(f.landAcres)}</span><span className="type-caption text-subtle">acres</span></span>
                    : undefined}
                </InlineEditField>
                <InlineEditField label="Crops" value={crops} type="select" multiple options={cropOptions}
                  className="sm:col-span-2" {...fieldProps('crops')}>
                  {crops.length ? <span className="flex flex-wrap gap-1">{crops.map((c) => <Tag tone="green" key={c}>{c}</Tag>)}</span> : undefined}
                </InlineEditField>
              </Group>

              <Group icon={<Users className="size-3.5" />} tint={SECTION_TINT.assignment} title="Who covers them">
                {/* State → District → Beat, each level its own colour — the same chips the farmers
                    register and every customer-bearing list now use. One violet chip carrying
                    "MH-JALGAON · Jalgaon" said where this farmer was only to somebody who already
                    knew the code. */}
                <InlineEditField label="Territory" value={f.territoryId} {...fieldProps('territoryId')}>
                  <TerritoryChips territoryId={f.territoryId} fallbackName={f.territoryName} />
                </InlineEditField>
                <InlineEditField label="Assigned sales person" value={f.assignedSalesPersonId} type="select"
                  options={spOptions} optionRender={renderSpOption} {...fieldProps('assignedSalesPersonId')}>
                  {spLabel ? (
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate">{spLabel}</span>
                      <RoleBadge role="SALES_PERSON" />
                    </span>
                  ) : undefined}
                </InlineEditField>
              </Group>

            </div>

            <div className="space-y-4">
              {/* Photo + KYC scan — otherwise only reachable through the edit form, which meant a
                  viewer without FARMER.EDIT could see "KYC pending" but never the document itself. */}
              <div className="rounded-card border border-line bg-surface p-4 shadow-e1">
                <SectionHead icon={<FileText className="size-3.5" />} tint={SECTION_TINT.documents}>Documents</SectionHead>
                <div className="grid grid-cols-2 gap-3">
                  <DocCard icon={<Camera size={14} />} title="Farmer photo" present={!!f.photoUrl}>
                    <InlineEditField label="Farmer photo" hideLabel type="file" uploadCategory="farmer-photo"
                      value={f.photoUrl} {...fieldProps('photoUrl')}>
                      {f.photoUrl
                        ? <DocumentPreview url={f.photoUrl} label="Farmer photo" />
                        : <span className="type-body text-warning-700 dark:text-harvest-300">No photo yet</span>}
                    </InlineEditField>
                  </DocCard>

                  <DocCard icon={<ShieldCheck size={14} />} title={kyc.length > 1 ? `KYC · ${kyc.length} files` : 'KYC document'} present={!!kyc.length}>
                    {/*
                      * Thumbnails, previewed in place. This showed a URL and an "Open" link before,
                      * which meant leaving the page to check a scan — and it only ever showed the
                      * FIRST document, so an Aadhaar's back side was invisible.
                      *
                      * The slot that says "not collected" is the slot that TAKES the document, so an
                      * editor gets one control here, not a thumbnail plus a separate empty "Add KYC
                      * document" field whose "—" read as broken.
                      */}
                    {kyc.length ? (
                      <>
                        <AttachmentCell files={kyc} label="KYC document" size={64} maxThumbs={2} />
                        {editableFields.has('kycDocUrl') && (
                          <div className="mt-2">
                            <InlineEditField label="Replace KYC document" type="file" uploadCategory="attachment"
                              value={f.kycDocUrl} {...fieldProps('kycDocUrl')}>
                              <span className="type-caption text-brand-700 dark:text-brand-400">Replace</span>
                            </InlineEditField>
                          </div>
                        )}
                      </>
                    ) : editableFields.has('kycDocUrl') ? (
                      <InlineEditField label="Add KYC document" hideLabel type="file" uploadCategory="attachment"
                        value={f.kycDocUrl} {...fieldProps('kycDocUrl')}>
                        <span className="type-body text-warning-700 dark:text-harvest-300">Not collected — add one</span>
                      </InlineEditField>
                    ) : (
                      <span className="type-body text-warning-700 dark:text-harvest-300">Not collected yet</span>
                    )}
                  </DocCard>
                </div>
              </div>

              <div className="rounded-card border border-line bg-surface p-4 shadow-e1">
                <SectionHead icon={<MapPin className="size-3.5" />} tint={SECTION_TINT.place}
                  hint={hasPin ? undefined : 'no pin'}>Location</SectionHead>
                {hasPin ? (
                  <div className="h-[300px] overflow-hidden rounded-control border border-line bg-surface-3">
                    {settled && (
                      <MapContainer center={[lat, lng]} zoom={13} style={{ height: 300, width: '100%' }} scrollWheelZoom={false}>
                        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
                        <FixSize />
                        <Marker position={[lat, lng]} icon={PIN} />
                      </MapContainer>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-1 rounded-control border border-warning-300/60 bg-warning-50/50 px-3 py-8 text-center dark:border-harvest-500/30 dark:bg-harvest-500/5">
                    <MapPin size={20} className="text-warning-600 dark:text-harvest-400" />
                    <div className="type-body text-muted">
                      No GPS pin{place ? <> for <b>{place}</b></> : ''}.
                    </div>
                    {gmaps && (
                      <a className="mt-1 inline-flex h-8 items-center justify-center gap-1.5 rounded-control px-3 type-body-strong leading-none text-content transition-colors duration-150 hover:bg-surface-3" href={gmaps} target="_blank" rel="noopener">
                        <ExternalLink size={14} /><span>Search this area on Google Maps</span>
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-card border border-line bg-surface p-4 shadow-e1">
                <Group icon={<CalendarDays className="size-3.5" />} tint={SECTION_TINT.system} title="Record">
                  <OnlyField label="Registered">{formatIstDate(f.createdAt)}</OnlyField>
                  <OnlyField label="Registered via">{f.registeredVia || undefined}</OnlyField>
                </Group>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
